import { Href, useRouter } from 'expo-router';
import { useMemo } from 'react';

import { Link, LinkText } from '@/components/ui/link';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ROUTES } from '@/constants/routes';
import { clearEHRSession, isEmbeddedFrame } from '@/util/smartLaunch';

/**
 * Where an EHR session lands when its Cognito session ends (/auth/smart/ended).
 *
 * The ordinary bounce is the login form, which is a dead end here: Google refuses to run in
 * the EHR's frame, and an account created from the EHR has no password. Reopening Primum from
 * the EHR mints a fresh session, so that is the way back this screen offers first.
 */
export default function SmartEndedScreen() {
  const { replace } = useRouter();

  const embedded = useMemo(isEmbeddedFrame, []);

  const openSignIn = () => {
    if (embedded) {
      window.open(ROUTES.LOGIN, '_blank', 'noopener');

      return;
    }

    clearEHRSession();

    replace(ROUTES.LOGIN as Href);
  };

  return (
    <VStack className="flex-1 items-center justify-center gap-3 p-6">
      <Text className="text-center font-semibold text-xl text-typography-900" role="alert">
        Your Primum session ended.
      </Text>

      <Text className="max-w-[420px] text-center text-typography-700">
        Reopen Primum from your EHR to continue.
      </Text>

      <Link onPress={openSignIn} className="mt-2">
        <LinkText size="sm" className="underline">
          Sign in another way
        </LinkText>
      </Link>
    </VStack>
  );
}
